/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useTranslation } from 'react-i18next';
import { StatusBadge, Text } from '@/shared/ui';
import { leadAdBadgeState } from '../types';
import type { CatalogPublication, LeadAdBadgeState } from '../types';

const BADGE_BY_STATE: Record<LeadAdBadgeState, { tone: 'success' | 'neutral'; labelKey: string }> = {
  active: { tone: 'success', labelKey: 'publications.leadAdActive' },
  paused: { tone: 'neutral', labelKey: 'publications.leadAdPaused' },
};

/**
 * The Lead Ad state of a post -- running or paused. A Lead Ad is an
 * ad on a post, so it is shown on the post's card and detail instead of as a
 * publication of its own.
 *
 * @param props - The post's catalog entry.
 * @returns The badge, or null when the post has no Lead Ad (whatever the reason).
 */
export function LeadAdBadge({ entry }: { entry: CatalogPublication }) {
  const { t } = useTranslation('@features/properties');
  const state = leadAdBadgeState(entry);
  if (!state) return null;
  const { tone, labelKey } = BADGE_BY_STATE[state];
  return (
    <Text as="span" data-testid={`lead-ad-badge-${state}`}>
      <StatusBadge tone={tone}>{t(labelKey)}</StatusBadge>
    </Text>
  );
}
